import React, { useState, useEffect } from "react";
import { Modal, ModalBody, ModalHeader, Table, Row, Col } from "reactstrap";
// componentes
import Pagination from "../../../components/Paginationv3";
import LoadingSkelentonTable from "../../../components/loadingSkeletonTable";
// hooks
import useDebounce from "../../../utils/hooks/useDebounce";
// services
import {
  getNotificationGroups,
  getNotificationLogins,
} from "../../../utils/services/notification";

export default function ModalNotificationRecipients(props) {
  const { isOpen, toggle, notification } = props;
  // tipo de destinatario: "grupos" | "logins". Fica null enquanto nao foi resolvido
  const [tipo, setTipo] = useState(null);
  // dados da listagem
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  // pesquisa da listagem
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  // paginacao
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [countData, setCountData] = useState(0);

  const SUCESSO_GRUPOS = "NOTIFICATION_GROUPS_FINDED";
  const SUCESSO_LOGINS = "NOTIFICATION_LOGINS_FINDED";

  // traduz a falha da api em uma mensagem para a tela
  function mensagemDeErro(res) {
    if (res?.message === "WRONG_AUTH_TOKEN") {
      return "Sua sessão expirou. Entre novamente para visualizar os destinatários.";
    }
    if (res?.message === "NOTIFICATION_API_ERROR") {
      return "O serviço de notificações está indisponível no momento. Tente novamente.";
    }
    return "Não foi possível carregar os destinatários.";
  }

  // descobre se a notificacao foi enviada para grupos ou para participantes. A regra
  // e excludente: havendo grupos, foi por grupo; caso contrario, por participante.
  // A resposta de grupos ja e aproveitada como primeira pagina quando houver
  async function resolveTipo() {
    setLoading(true);
    setErro("");

    const grupos = await getNotificationGroups({
      id: notification.id,
      offset: 1,
      limit: itemsPerPage,
      search: "",
    });

    if (grupos?.message !== SUCESSO_GRUPOS) {
      setErro(mensagemDeErro(grupos));
      setLoading(false);
      return;
    }

    if (grupos.findOneNotificationGroupsData.count > 0) {
      setTipo("grupos");
      setItems(grupos.findOneNotificationGroupsData.data);
      setCountData(grupos.findOneNotificationGroupsData.count);
      setLoading(false);
      return;
    }

    const logins = await getNotificationLogins({
      id: notification.id,
      offset: 1,
      limit: itemsPerPage,
      search: "",
    });

    if (logins?.message !== SUCESSO_LOGINS) {
      setErro(mensagemDeErro(logins));
      setLoading(false);
      return;
    }

    setTipo("logins");
    setItems(logins.findOneNotificationLoginsData.data);
    setCountData(logins.findOneNotificationLoginsData.count);
    setLoading(false);
  }

  // busca a pagina atual do tipo ja resolvido
  async function getRecipients() {
    setLoading(true);
    setErro("");

    const busca = {
      id: notification.id,
      offset: currentIndex,
      limit: itemsPerPage,
      search: debouncedSearch,
    };

    if (tipo === "grupos") {
      const res = await getNotificationGroups(busca);
      if (res?.message === SUCESSO_GRUPOS) {
        setItems(res.findOneNotificationGroupsData.data);
        setCountData(res.findOneNotificationGroupsData.count);
      } else {
        setItems([]);
        setCountData(0);
        setErro(mensagemDeErro(res));
      }
    } else {
      const res = await getNotificationLogins(busca);
      if (res?.message === SUCESSO_LOGINS) {
        setItems(res.findOneNotificationLoginsData.data);
        setCountData(res.findOneNotificationLoginsData.count);
      } else {
        setItems([]);
        setCountData(0);
        setErro(mensagemDeErro(res));
      }
    }

    setLoading(false);
  }

  // atualiza o termo pesquisado e volta para a primeira pagina, senao a busca sairia
  // com o offset da pagina em que o usuario estava
  function handleSearch(value) {
    setSearch(value);
    setCurrentIndex(1);
  }

  // participante ou grupo excluido depois do envio volta com deleted: true e o nome
  // nulo. Continua na lista para o total bater com a listagem
  function nomeDoItem(item) {
    if (item.deleted) {
      return <i style={{ color: "#74788d" }}>(removido)</i>;
    }
    return tipo === "grupos" ? item.name : item.nome;
  }

  useEffect(() => {
    resolveTipo();
  }, []);

  useEffect(() => {
    // enquanto o tipo nao foi resolvido, a primeira pagina ja veio do resolveTipo.
    // E enquanto o debounce nao fecha, search e debouncedSearch divergem e o fetch
    // sairia com o termo antigo
    if (!tipo) return;
    if (search !== debouncedSearch) return;

    getRecipients();
  }, [debouncedSearch, currentIndex, itemsPerPage]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered autoFocus size="lg">
      <ModalHeader toggle={toggle}>
        {/* enquanto o tipo nao foi resolvido nao da para afirmar o destino, entao
        o titulo fica neutro para nao exibir a informacao errada por um instante */}
        {!tipo
          ? "Push enviado para:"
          : `Push enviado para: ${tipo === "grupos" ? "Grupos" : "Participantes"}`}
      </ModalHeader>
      <ModalBody>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: "bold" }}>Notificação: </span>
          <span>{notification.title}</span>
        </div>

        <form
          className="app-search"
          style={{ padding: 0, width: "100%" }}
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder={
                tipo === "grupos"
                  ? "Pesquisar por nome do grupo..."
                  : "Pesquisar por nome ou e-mail..."
              }
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <span className="bx bx-search-alt" />
          </div>
        </form>

        {erro && (
          <span style={{ color: "red", display: "block", marginTop: 10 }}>
            {erro}
          </span>
        )}

        <div className="table-responsive" style={{ marginTop: 10 }}>
          <Table className="table mb-0">
            <thead className="table-light">
              <tr>
                <th>Nome</th>
                {tipo === "logins" && <th>E-mail</th>}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <LoadingSkelentonTable
                  lines={3}
                  cols={tipo === "logins" ? 2 : 1}
                />
              ) : items.length > 0 ? (
                items.map((e, i) => (
                  <tr key={i}>
                    <td>{nomeDoItem(e)}</td>
                    {tipo === "logins" && <td>{e.deleted ? "" : e.email}</td>}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tipo === "logins" ? 2 : 1}>
                    {debouncedSearch
                      ? "Nenhum resultado para a pesquisa."
                      : "Esta notificação não possui destinatários registrados."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <Row style={{ marginTop: 20 }}>
          <Col md={12}>
            <Pagination
              data={items}
              search={search}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              countData={countData}
              maxItemPerPage={itemsPerPage}
              setMaxItemPerPage={setItemsPerPage}
              maxPageButtonsToShow={3}
            />
          </Col>
        </Row>
      </ModalBody>
    </Modal>
  );
}
